import { Component } from "react"
import { v4 as uuid } from "uuid"
import PropTypes from "prop-types"
import classNames from "classnames"
import { connect } from "react-redux"
import { get } from "lodash"
import { findClosestDatum } from "../helpers/find-closest-datum"
import moment from "moment"
import resizable from "hocs/resizable"
import tooltipContainer from "hocs/tooltip-container"
import Tooltip from "components/charts/tooltip"
import { HIGHLIGHTER } from "../shapes"
import LegendItem from "./legend-item"
import ChartDefs from "./components/chart-defs"
import Axis from "./axis"
import Dots from "./dots"
import { getTotalLine, getTotalYAxis, getFindTotalByOffset } from "./selectors/total"
import { getBreakdownAreas, getBreakdownYAxis, getBreakdownScaleY, getFindBreakdownByOffset } from "./selectors/breakdown"
import { getXAxis, getScaleX, getTotalScaleY, getHighlighter } from "./selectors"
import { getBreakdownItems, getTotalItem, getOnlyLegendText } from "./selectors/items"
import "./style.scss"

// Intraday: a wide transparent hit band along a line, so the drilldown can be opened by
// clicking the line anywhere — capturing that exact line's id — rather than landing on a
// tiny, densely-packed dot. Shared by both the breakdown lines and the total line.
function LineHitArea({ d, lineId, lineLabel, focusChartLine, blurChartLine, onDotClick }) {
  return (
    <path
      d={d}
      className="line-hit-area"
      role="button"
      aria-label={lineLabel ? `Open drilldown for ${lineLabel}` : undefined}
      onMouseEnter={() => focusChartLine(lineId)}
      onMouseLeave={blurChartLine}
      onClick={(event) => onDotClick(event, lineId)}
    />
  )
}

LineHitArea.propTypes = {
  d: PropTypes.string.isRequired,
  lineId: PropTypes.string.isRequired,
  lineLabel: PropTypes.string,
  focusChartLine: PropTypes.func.isRequired,
  blurChartLine: PropTypes.func.isRequired,
  onDotClick: PropTypes.func.isRequired,
}

const AXIS = PropTypes.shape({
  fn: PropTypes.func.isRequired,
  transform: PropTypes.string.isRequired,
  baseline: PropTypes.shape({
    x1: PropTypes.number.isRequired,
    x2: PropTypes.number.isRequired,
    y1: PropTypes.number.isRequired,
    y2: PropTypes.number.isRequired,
  }).isRequired,
})

export class MultiLineBreakdownChart extends Component {
  static propTypes = {
    totalLine: PropTypes.object,
    data: PropTypes.array.isRequired,
    totalItem: PropTypes.array.isRequired,
    breakdownAreas: PropTypes.array.isRequired,
    highlighter: HIGHLIGHTER,
    width: PropTypes.number,
    resizableRef: PropTypes.func.isRequired,
    xAxis: AXIS.isRequired,
    yAxis: AXIS.isRequired,
    yBreakdownAxis: AXIS.isRequired,
    items: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
    })).isRequired,
    tooltip: PropTypes.element.isRequired,
    scaleX: PropTypes.func.isRequired,
    totalScaleY: PropTypes.func.isRequired,
    toggle: PropTypes.func.isRequired,
    subscribe: PropTypes.func.isRequired,
    unsubscribe: PropTypes.func.isRequired,
    setDate: PropTypes.func.isRequired,
    focusChartLine: PropTypes.func.isRequired,
    blurChartLine: PropTypes.func.isRequired,
    findBreakdownByOffset: PropTypes.func.isRequired,
    findTotalByOffset: PropTypes.func.isRequired,
    focusedChartLine: PropTypes.string,
    totalLabel: PropTypes.string,
    breakdownLabel: PropTypes.string,
    toggleOnly: PropTypes.func.isRequired,
    onlyLegendText: PropTypes.string.isRequired,
    onDotClick: PropTypes.func,
    isOneDayMode: PropTypes.bool,
    // consumed by getHighlighter via mapStateToProps, not destructured in render()
    isPeriodIntraday: PropTypes.bool,
  }

  constructor(props) {
    super(props)

    const chartId = uuid()
    this.pathGradientId = `path-gradient-${chartId}`
    this.axisGradientId = `axis-gradient-${chartId}`
    this.areaGradientId = `area-gradient-${chartId}`
  }

  legendRef = null

  setDate = offset => {
    const { setDate } = this.props
    const datum = this.findByOffset(offset)
    if (datum) setDate(moment(datum.date).format("YYYY-MM-DD"))
  }

  findByOffset = offset => {
    const { data, scaleX } = this.props
    const lookup = scaleX.invert(offset - 18)
    return findClosestDatum(data, lookup)
  }

  handleGraphClick = ev => {
    const box = ev.currentTarget.getBoundingClientRect()
    const offsetX = ev.clientX - box.left + 16
    this.setDate(offsetX)
  }

  handleDotClick = (event, lineId) => {
    event.stopPropagation()
    const box = event.currentTarget.closest("svg").getBoundingClientRect()
    // clientX (not pageX): box is viewport-relative, so pageX over-shoots by the horizontal scroll.
    const offsetX = event.clientX - box.left
    const datum = this.findByOffset(offsetX)
    // datum.date is a local-midnight moment built from a YYYY-MM-DD batch string (analyze-risk selectors).
    // Local-mode format gives the correct batch date in any timezone.
    const date = datum ? moment(datum.date).format("YYYY-MM-DD") : undefined
    // batchId survives the day-level date format above, so batch-trend points stay disambiguable.
    this.props.onDotClick?.({ clientX: event.clientX, clientY: event.clientY, date, batchId: datum?.batchId }, lineId)
  }

  computeAxisStyle = () => {
    const { highlighter, width } = this.props
    const axisStyle = {}

    if (highlighter && width) {
      axisStyle.stroke = `url(#${this.axisGradientId})`
    }

    return axisStyle
  }

  findTotalDots = offset => {
    const { scaleX, totalScaleY, totalItem, findTotalByOffset } = this.props
    const datum = findTotalByOffset(offset)

    if (datum) {
      return totalItem
        .map(item => ({
          id: item.id,
          color: item.color,
          y: totalScaleY(get(datum, item.id, 0)),
          x: scaleX(datum.date),
        }))
    }
  }

  findBreakdownDots = offset => {
    const { findBreakdownByOffset } = this.props
    const datum = findBreakdownByOffset(offset)

    if (datum.length) return datum
  }

  computeStyle = (id, initialStyle, property) => {
    const { highlighter, width } = this.props
    const pathStyle = {}

    if (highlighter && width) {
      pathStyle[property] = `url(#${id})`
    } else {
      pathStyle[property] = initialStyle
    }

    return pathStyle
  }

  registerLegendRef = element => { this.legendRef = element }

  render() {
    const {
      breakdownAreas,
      resizableRef,
      xAxis,
      yAxis,
      yBreakdownAxis,
      items,
      totalLine,
      toggle,
      focusedChartLine,
      focusChartLine,
      blurChartLine,
      subscribe,
      tooltip,
      highlighter,
      unsubscribe,
      width,
      totalLabel,
      breakdownLabel,
      data,
      toggleOnly,
      onlyLegendText,
      onDotClick,
      isOneDayMode,
    } = this.props

    if (!data.length) {
      return <div ref={resizableRef} className="components_charts_multi-line-breakdown no-data">No significant data was found. Adjust the date range to a larger time frame to view graph.</div>
    } else {
      return <div className="components_charts_multi-line-breakdown">
        <div className="legend" ref={this.registerLegendRef}>
          {items.map(item =>
            <LegendItem
              onlyLegendText={onlyLegendText}
              key={item.id}
              {...item}
              onClick={toggle}
              onOnlyToggle={toggleOnly}
              focus={focusChartLine}
              blur={blurChartLine}
              focused={focusedChartLine}
            />,
          )}
        </div>
        <svg
          ref={resizableRef}
          onClick={isOneDayMode ? undefined : this.handleGraphClick}
          className={classNames(isOneDayMode && "static")}
        >
          <ChartDefs
            totalLine={totalLine}
            layers={breakdownAreas}
            axisId={this.axisGradientId}
            pathsId={this.pathGradientId}
            areasId={this.areaGradientId}
            offset={highlighter && highlighter.x1}
            chartWidth={width}
          />
          <Axis {...xAxis} />
          <Axis {...yAxis} className="total-axis" title={totalLabel} />
          <Axis {...yBreakdownAxis} className="breakdown-axis" title={breakdownLabel} position="right" />
          <line {...xAxis.baseline} className="baseline" style={this.computeAxisStyle()} />
          <line {...yAxis.baseline} className="baseline" />
          <g>
            {breakdownAreas.map((layer, i) =>
              <g key={layer.id}>
                {layer.line
                  && <>
                    <path
                      className={classNames(
                        "path focus-enabled area-path",
                        {
                          visible: focusedChartLine && focusedChartLine === layer.id,
                          hidden: focusedChartLine && focusedChartLine !== layer.id,
                          "single-dot": data && data.length === 1,
                        },
                      )}
                      {...layer.line}
                      style={{ ...this.computeStyle(`${this.pathGradientId}-line-${i}`, layer.line.style.stroke, "stroke") }}
                    />
                    {isOneDayMode && onDotClick && <LineHitArea
                      d={layer.line.d}
                      lineId={layer.id}
                      lineLabel={items.find(item => item.id === layer.id)?.label}
                      focusChartLine={focusChartLine}
                      blurChartLine={blurChartLine}
                      onDotClick={this.handleDotClick}
                    />}
                  </>}
                <path
                  {...layer.area}
                  style={{ ...this.computeStyle(`${this.areaGradientId}-area-${i}`, layer.area.style.fill, "fill"), pointerEvents: "none" }}
                />
              </g>,
            )}
            <Dots
              {...{
                subscribe,
                unsubscribe,
                findDots: this.findBreakdownDots,
                focus: focusChartLine,
                blur: blurChartLine,
                focused: focusedChartLine,
                onDotClick: onDotClick ? this.handleDotClick : undefined,
              }}
            />
          </g>
          {totalLine
            && <g>
              <path
                className={classNames(
                  "path focus-enabled",
                  {
                    visible: focusedChartLine && focusedChartLine === totalLine.id,
                    hidden: focusedChartLine && focusedChartLine !== totalLine.id,
                    "single-dot": data && data.length === 1,
                  },
                )}
                {...totalLine}
                style={{ ...this.computeStyle(`${this.pathGradientId}-total`, totalLine.style.stroke, "stroke") }}
              />
              {isOneDayMode && onDotClick && <LineHitArea
                d={totalLine.d}
                lineId={totalLine.id}
                lineLabel={totalLabel}
                focusChartLine={focusChartLine}
                blurChartLine={blurChartLine}
                onDotClick={this.handleDotClick}
              />}
              <Dots
                {...{
                  subscribe,
                  unsubscribe,
                  findDots: this.findTotalDots,
                  focus: focusChartLine,
                  blur: blurChartLine,
                  focused: focusedChartLine,
                  onDotClick: onDotClick ? this.handleDotClick : undefined,
                }}
              />
            </g>}
          {highlighter && <line className="highlighter" {...highlighter} />}
        </svg>
        <Tooltip {...{ legendRef: this.legendRef, subscribe, unsubscribe, findByOffset: this.findByOffset, position: "right" }}>
          {tooltip}
        </Tooltip>
      </div>
    }
  }
}

const mapStateToProps = (state, props) => ({
  totalLine: getTotalLine(props),
  breakdownAreas: getBreakdownAreas(props),
  xAxis: getXAxis(props),
  yAxis: getTotalYAxis(props),
  scaleX: getScaleX(props),
  totalScaleY: getTotalScaleY(props),
  breakdownScaleY: getBreakdownScaleY(props),
  breakdownItems: getBreakdownItems(props),
  totalItem: getTotalItem(props),
  findTotalByOffset: getFindTotalByOffset(props),
  findBreakdownByOffset: getFindBreakdownByOffset(props),
  yBreakdownAxis: getBreakdownYAxis(props),
  highlighter: getHighlighter(props),
  onlyLegendText: getOnlyLegendText(props),
})

export default tooltipContainer(resizable(connect(mapStateToProps)(MultiLineBreakdownChart)))
